import Constants from '@/Constants';
import { analyticsService, PlaylistEvents } from '@/services/AnalyticsService';
import { ApplicationState } from '@/store';
import { closeModal } from '@/store/common/actions';
import { toggleListVisibilityRequest } from '@/store/list/action';
import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import './style.scss';

enum Option {
    PRIVATE = 1,
    PUBLIC = 2,
}

interface VisibilityOptions {
    id: Option;
    icon: string;
    description: string;
    subdescription: string;
    checked: boolean;
}

const ChangeVisibilityModal: React.FC = () => {
    const { strings, course } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        course: state.lists.currentData.course,
    }));

    const [visibilityOptions, setVisibilityOptions] = useState<VisibilityOptions[]>([
        {
            id: Option.PRIVATE,
            icon: Constants.icons.LOCK,
            description: strings.PRIVATE,
            subdescription: strings.ONLY_ME,
            checked: false,
        },
        {
            id: Option.PUBLIC,
            icon: Constants.icons.PUBLIC,
            description: strings.PUBLIC,
            subdescription: strings.EVERYONE_IN_MY_ORGANIZATION,
            checked: false,
        },
    ]);

    const [initialOption, setInitialOption] = useState<Option>(Option.PRIVATE);
    const [selectedOption, setSelectedOption] = useState<Option>(Option.PRIVATE);

    const dispatch = useDispatch();

    const selectOption = (option: Option) => {
        visibilityOptions.forEach((visibilityOption: VisibilityOptions) => {
            visibilityOption.checked = visibilityOption.id === option;
        });

        setSelectedOption(option);
        setVisibilityOptions([...visibilityOptions]);
    };

    const dismissModalHandler = () => {
        dispatch(closeModal(Constants.modal.MORE_OPTIONS));
    };

    const onConcludeHandler = () => {
        if (!course.visibility) {
            analyticsService.logEvent(PlaylistEvents.PUBLISH_LIST);
        }
        dispatch(toggleListVisibilityRequest(course.id as number));
        dismissModalHandler();
    };

    useEffect(() => {
        const option = course.visibility ? Option.PUBLIC : Option.PRIVATE;
        selectOption(option);
        setInitialOption(option);
    }, [course.visibility]);

    return (
        <div className="change-visibility-modal">
            <Modal.Header>
                <div className="header-info">
                    <Modal.Title>
                        <span className="title">{strings.CHANGE_VISIBILITY}</span>
                        <i className="material-icons-outlined align-middle" onClick={dismissModalHandler}>
                            {Constants.icons.CLOSE}
                        </i>
                    </Modal.Title>
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className="visibility-options">
                    {visibilityOptions.map((option: VisibilityOptions) => (
                        <div className="option" key={option.id}>
                            <i className="material-icons align-middle check mr-3" onClick={() => selectOption(option.id)}>
                                {option.checked ? Constants.icons.RADIO_BUTTON_CHECKED : Constants.icons.RADIO_BUTTON_UNCHECKED}
                            </i>
                            <div className="d-flex flex-column ml-1">
                                <div className="d-flex flex-row">
                                    <span className={`${option.checked ? 'description-selected' : ''}`}>{option.description}</span>
                                    <i className="material-icons align-middle visibility-icon ml-1">{option.icon}</i>
                                </div>
                                <span className={`subdescription ${option.checked ? 'subdescription-selected' : ''}`}>{option.subdescription}</span>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="d-flex flex-column">
                    {selectedOption === Option.PUBLIC && !course.visibilityDate && (
                        <div className="alert-container">
                            <i className="material-icons-outlined align-middle mr-2">{Constants.icons.REPORT_PROBLEM}</i>
                            <span dangerouslySetInnerHTML={{ __html: strings.CHANGE_VISIBILITY_PUBLIC_ALERT }} />
                        </div>
                    )}
                    <button className="btn btn-primary" disabled={initialOption === selectedOption} onClick={onConcludeHandler}>
                        {strings.CHANGE}
                    </button>
                </div>
            </Modal.Body>
        </div>
    );
};

export default ChangeVisibilityModal;
